import { MoreVert, OpenInNew, Pause, PlayCircleOutlineOutlined, StopCircleOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, IconButton, Link, Menu, MenuItem, SxProps, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import BottomPanel from '../bottom-panel/BottomPanel';
import { ExerciseType, SimulationRole, SimulationSignal, SimulationState } from '../types';
import { getSimulationPath, isScheduleExercise } from '../utils/activeSimulation';

export interface SimulatorControlsProps {
  exercise: { id?: string; name: string; type: ExerciseType };
  id: string;
  isStudent?: boolean;
  name?: string; // In practice the name is defined
  onClickControl: (stateChange: SimulationSignal) => void;
  role: SimulationRole;
  simulator: { name?: string };
  state?: SimulationState;
  url?: string;
  waiting?: boolean;
}

const buttonStyles: SxProps = { flex: { xs: 1, sm: 'inherit' }, width: { sm: 128 } };

const SimulatorControls = ({
  exercise: { id: exerciseId, name: exerciseName, type },
  id,
  isStudent,
  name,
  onClickControl,
  role,
  simulator: { name: simulatorName },
  state,
  url = '',
  waiting,
}: SimulatorControlsProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const showControls = role === SimulationRole.Instructor;

  return (
    <BottomPanel sx={{ minHeight: { xs: '144px', sm: '136px', md: '72px' } }}>
      <Box display={{ sm: 'flex' }} alignItems="center" columnGap={8} rowGap={3} flexWrap="wrap">
        <Box display={{ xs: 'none', sm: 'block' }}>
          <Typography variant="overline" sx={{ lineHeight: 1 }}>
            Simulator in use
          </Typography>
          <Typography component="div" variant="body2">
            {simulatorName}
          </Typography>
        </Box>
        <Box flex={{ xs: 'inherit', sm: 1 }}>
          {isScheduleExercise(exerciseId) ? (
            <Link
              component={RouterLink}
              sx={{ lineHeight: 1 }}
              to={getSimulationPath(isStudent, exerciseId, id, role, type)}
              variant="overline"
            >
              {name}
            </Link>
          ) : (
            <Typography component="div">{name}</Typography>
          )}
          <Typography component="div" variant="body2">
            {exerciseName}
          </Typography>
        </Box>

        <Box display="flex" gap={1} mt={{ xs: 4, sm: 0 }}>
          {showControls && (
            <>
              <LoadingButton
                disabled={waiting}
                onClick={() => onClickControl(SimulationSignal.Stop)}
                sx={buttonStyles}
                variant="outlined"
                endIcon={<StopCircleOutlined />}
              >
                Stop
              </LoadingButton>
              {state === SimulationState.Running ? (
                <LoadingButton
                  disabled={waiting}
                  onClick={() => onClickControl(SimulationSignal.Pause)}
                  sx={buttonStyles}
                  variant="outlined"
                  endIcon={<Pause />}
                >
                  Pause
                </LoadingButton>
              ) : (
                <LoadingButton
                  disabled={waiting}
                  onClick={() => onClickControl(SimulationSignal.Play)}
                  variant="contained"
                  endIcon={<PlayCircleOutlineOutlined />}
                >
                  Play
                </LoadingButton>
              )}
            </>
          )}
          {showControls && url && (
            <Button
              startIcon={<OpenInNew />}
              href={url}
              rel="noreferrer"
              target="_blank"
              variant="outlined"
              sx={{ display: { xs: showControls ? 'none' : 'inherit', sm: 'inherit' } }}
            >
              Open
            </Button>
          )}

          {(showControls || url) && (
            <IconButton
              aria-label="More actions"
              aria-controls={menuAnchor ? 'exercise-controls-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuAnchor ? 'true' : undefined}
              id="exercises-controls-menu-button"
              onClick={event => setMenuAnchor(event.currentTarget)}
            >
              <MoreVert />
            </IconButton>
          )}
          <Menu
            anchorEl={menuAnchor}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            id="exercise-controls-menu"
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            slotProps={{
              root: { style: { backgroundColor: 'transparent' } },
            }}
            MenuListProps={{ 'aria-labelledby': 'exercises-controls-menu-button' }}
          >
            {showControls && (
              <MenuItem
                dense
                onClick={() => {
                  setMenuAnchor(null);
                  onClickControl(SimulationSignal.Abandon);
                }}
                sx={theme => ({ color: theme.palette.error.main })}
              >
                Abort simulation
              </MenuItem>
            )}
            {url && (
              <MenuItem
                dense
                onClick={() => {
                  setMenuAnchor(null);
                }}
                sx={{ display: { sm: showControls ? 'none' : null } }}
              >
                <Box
                  component="a"
                  href={url}
                  rel="noreferrer"
                  target="_blank"
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                  Open in new tab
                </Box>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
    </BottomPanel>
  );
};

export default SimulatorControls;
